/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { PROFILE_PUBLIC_COLUMNS } from "@/lib/profileColumns";
import { useAuth } from "@/hooks/useAuth";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { computeProAccessStatus, type ProAccessStatus } from "@/lib/proAccessRules";

export interface MissingField {
  key: string;
  label: string;
  tab: string;
  fieldId?: string;
}

export interface ProAccessState {
  status: ProAccessStatus;
  emailVerified: boolean;
  profileCompleted: boolean;
  profileSecondaryCompleted: boolean;
  identityDocumentsSubmitted: boolean;
  identityVerificationStatus: "not_started" | "pending" | "approved" | "rejected";
  rejectionReason: string | null;
  subscriptionActive: boolean;
  subscriptionRequired: boolean;
  missingFields: string[];
  missingFieldsDetailed: MissingField[];
}

// Le tunnel d'inscription ne bloque que sur les informations réellement essentielles.
// Les réglages détaillés de l'onglet Pro restent modifiables plus tard.
export const REQUIRED_PROFILE_FIELDS: MissingField[] = [
  { key: "full_name", label: "Prénom et nom", tab: "profile", fieldId: "fullName" },
  { key: "avatar_url", label: "Photo de profil", tab: "profile", fieldId: "avatar-upload" },
  { key: "phone", label: "Numéro de téléphone", tab: "profile", fieldId: "phone" },
  { key: "city", label: "Ville", tab: "profile", fieldId: "city" },
];

export function useProAccess(): ProAccessState {
  const { user, role, subscriptionActive, loading: authLoading } = useAuth();
  const { getSetting, loading: settingsLoading } = useSiteSettings();
  const subscriptionRequired = getSetting("pro_subscription_enabled", "true") !== "false";
  const effectiveSubscription = subscriptionRequired ? subscriptionActive : true;
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    const handler = () => setRefreshTick((tick) => tick + 1);
    window.addEventListener("pro-access:refresh", handler);
    return () => window.removeEventListener("pro-access:refresh", handler);
  }, []);

  const [state, setState] = useState<ProAccessState>({
    status: "loading",
    emailVerified: false,
    profileCompleted: false,
    profileSecondaryCompleted: false,
    identityDocumentsSubmitted: false,
    identityVerificationStatus: "not_started",
    rejectionReason: null,
    subscriptionActive: false,
    subscriptionRequired: true,
    missingFields: [],
    missingFieldsDetailed: [],
  });

  useEffect(() => {
    if (authLoading || settingsLoading) return;
    if (!user || role !== "pro") {
      setState((current) => ({ ...current, status: "not_pro", subscriptionRequired }));
      return;
    }

    let cancelled = false;
    const compute = async () => {
      const [{ data: profile }, { data: ownPhone }, { data: privateProfile }, { data: verification }] = await Promise.all([
        supabase.from("profiles").select(PROFILE_PUBLIC_COLUMNS as any).eq("user_id", user.id).maybeSingle<any>(),
        supabase.rpc("get_my_phone"),
        supabase.rpc("get_my_private_profile"),
        supabase.from("identity_verifications").select("status, rejection_reason, id_card_url, id_card_verso_url, selfie_url").eq("user_id", user.id).maybeSingle(),
      ]);
      if (cancelled) return;

      const privateRow = Array.isArray(privateProfile) ? privateProfile[0] : privateProfile;
      const merged: Record<string, any> = { ...(profile || {}), ...(privateRow || {}), phone: ownPhone ?? (profile as any)?.phone };
      const missingFieldsDetailed = REQUIRED_PROFILE_FIELDS.filter((field) => {
        const value = merged[field.key];
        return !value || (typeof value === "string" && !value.trim());
      });
      const profileCompleted = missingFieldsDetailed.length === 0;
      // Conservé pour compatibilité avec les anciens composants : cette étape n'est plus bloquante.
      const profileSecondaryCompleted = true;
      const hasIdFront = Boolean(verification?.id_card_url);
      const hasIdBack = Boolean(verification?.id_card_verso_url);
      const hasSelfie = Boolean(verification?.selfie_url);
      const identityDocumentsSubmitted = hasIdFront && hasIdBack && hasSelfie && Boolean(profile?.avatar_url);
      const identityVerificationStatus: ProAccessState["identityVerificationStatus"] =
        !verification ? "not_started" : verification.status === "verified" ? "approved" : verification.status === "rejected" ? "rejected" : verification.status === "pending" ? "pending" : "not_started";
      const emailVerified = Boolean(user.email_confirmed_at);
      const missingFields = missingFieldsDetailed.map((field) => field.label);

      const status = computeProAccessStatus({
        subscriptionRequired,
        subscriptionActive,
        profileCompleted,
        identityVerificationStatus,
      });

      setState({
        status,
        emailVerified,
        profileCompleted,
        profileSecondaryCompleted,
        identityDocumentsSubmitted,
        identityVerificationStatus,
        rejectionReason: verification?.rejection_reason ?? null,
        subscriptionActive: effectiveSubscription,
        subscriptionRequired,
        missingFields,
        missingFieldsDetailed,
      });
    };

    void compute();
    return () => { cancelled = true; };
  }, [user, role, subscriptionActive, authLoading, settingsLoading, subscriptionRequired, effectiveSubscription, refreshTick]);

  return state;
}
